import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { useSetupDraft } from "@/hooks/useSetupDraft";
import { locationDraftStorageKey } from "@/lib/setup-draft-storage";

interface LocationRow {
  id: string;
  name: string;
  code: string | null;
  type: string;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  contact_name: string | null;
  contact_email: string | null;
  is_active: boolean;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const emptyLocationForm = {
  open: false,
  name: "",
  code: "",
  type: "warehouse",
  address_line1: "",
  city: "",
  state: "",
  country: "AU",
  contact_name: "",
  contact_email: "",
};

export default function Locations() {
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft, clearDraft] = useSetupDraft(locationDraftStorageKey(org?.id), emptyLocationForm);
  const open = draft.open;
  const form = draft;

  useCreateDeepLink(() => setDraft((d) => ({ ...d, open: true })));

  const { data: locations, isLoading, isError, refetch } = useQuery({
    queryKey: ["locations", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("locations")
        .select("id, name, code, type, address_line1, city, state, country, contact_name, contact_email, is_active")
        .eq("org_id", org!.id)
        .order("name");
      return (data ?? []) as LocationRow[];
    },
  });

  const set = (k: keyof typeof emptyLocationForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: insErr } = await supabase.from("locations").insert({
      org_id: org!.id,
      name: form.name,
      code: form.code || null,
      type: form.type,
      address_line1: form.address_line1 || null,
      city: form.city || null,
      state: form.state || null,
      country: form.country || null,
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
    });
    setSaving(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    clearDraft();
    queryClient.invalidateQueries({ queryKey: ["locations", org?.id] });
  };

  return (
    <div>
      <PageHeader title="Locations" subtitle="Manage warehouses and stock locations">
        <button onClick={() => setDraft((d) => ({ ...d, open: true }))} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-add-line" />
          Add Location
        </button>
      </PageHeader>

      {isLoading ? (
        <ListSkeleton />
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load locations.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !locations || locations.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState title="No locations" description="Add your first warehouse or store." />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className={l.type === "store" ? "ri-store-2-line text-emerald-600" : "ri-building-2-line text-emerald-600"} />
                </div>
                <StatusBadge status={l.is_active ? "active" : "inactive"} />
              </div>
              <Link to={`/locations/${l.id}`} className="text-sm font-semibold text-slate-900 hover:text-emerald-700 hover:underline">{l.name}</Link>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{l.code ?? "—"}</p>
              <p className="text-xs text-slate-500 mt-2 capitalize">{l.type}</p>
              <p className="text-xs text-slate-500 mt-1">
                {[l.address_line1, l.city, l.state, l.country].filter(Boolean).join(", ") || "No address"}
              </p>
              {l.contact_name && (
                <p className="text-xs text-slate-400 mt-2">{l.contact_name}{l.contact_email ? ` · ${l.contact_email}` : ""}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setDraft((d) => ({ ...d, open: false }))} title="Add Location"
        footer={
          <>
            <button onClick={() => setDraft((d) => ({ ...d, open: false }))} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Location"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Main Warehouse" />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input value={form.code} onChange={set("code")} className={inputCls} placeholder="WH-MAIN" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.type} onChange={set("type")} className={inputCls}>
                <option value="warehouse">Warehouse</option>
                <option value="store">Store</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Country</label>
              <input value={form.country} onChange={set("country")} className={inputCls} placeholder="AU" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Address</label>
            <input value={form.address_line1} onChange={set("address_line1")} className={inputCls} placeholder="1200 Industrial Blvd" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>City</label>
              <input value={form.city} onChange={set("city")} className={inputCls} placeholder="Sydney" />
            </div>
            <div>
              <label className={labelCls}>State</label>
              <input value={form.state} onChange={set("state")} className={inputCls} placeholder="NSW" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Contact name</label>
              <input value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Jane Smith" />
            </div>
            <div>
              <label className={labelCls}>Contact email</label>
              <input type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} placeholder="jane@company.com" />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}